import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { Player, PlayerReview, GamificationStats } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';
import { formatDateShort, formatReviewTimestamp } from '../utils/helpers';
import { FifaCard } from './FifaCard';

interface PlayerProfileModalProps {
  player: Player;
  isVisible: boolean;
  onClose: () => void;
}

export const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({
  player,
  isVisible,
  onClose
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [playerStats, setPlayerStats] = useState<any>(null);
  const [playerReviews, setPlayerReviews] = useState<PlayerReview[]>([]);
  const [gamificationStats, setGamificationStats] = useState<GamificationStats | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isVisible && player) {
      fetchPlayerData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, player]);

  const fetchPlayerData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Obtener datos del usuario si tiene userId
      if (player.userId) {
        
        // Usar el endpoint público para obtener datos del usuario
        const userResponse = await fetch(getApiUrl(`/api/users/${player.userId}/public-profile`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (userResponse.ok) {
          const data = await userResponse.json();
          setUserData(data.user);
          setPlayerStats(data.stats);
        } else {
          setUserData(null);
          setPlayerStats(null);
        }
      } else {
        setUserData(null);
        setPlayerStats(null);
      }
      
      // Obtener reviews y gamificación si el jugador tiene userId
      if (player.userId) {

              // Obtener reviews del jugador
      const reviewsResponse = await fetch(getApiUrl(`/api/reviews/user/${player.userId}/profile-reviews`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json();
        // Tomar las últimas 5 reviews
        setPlayerReviews(reviewsData.reviews?.slice(0, 5) || []);
      }

        // Obtener estadísticas de gamificación
        const gamificationResponse = await fetch(getApiUrl(`/api/gamification/user/${player.userId}/gaming-stats`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (gamificationResponse.ok) {
          const gamification = await gamificationResponse.json();
          setGamificationStats(gamification.stats);
        }
      } else {
        // Para jugadores agregados por otros usuarios, limpiar los datos
        setPlayerStats(null);
        setPlayerReviews([]);
        setGamificationStats(null);
      }

    } catch (error) {
      logger.error('Error fetching player data:', error);
      showNotification('Error cargando datos del jugador', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTagColor = (tag: string, isPositive: boolean) => {
    return isPositive ? '#00E676' : '#FF6B6B';
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        maxWidth: isMobile ? '100%' : '800px',
        width: '100%',
        maxHeight: '90vh',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header fijo */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '16px' : theme.spacing.lg,
          paddingBottom: '12px',
          borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
          backgroundColor: theme.colors.surface,
          borderRadius: `${theme.borderRadius.card} ${theme.borderRadius.card} 0 0`,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <h2 style={{
            margin: 0,
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle
          }}>
            👤 Perfil de {player.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              fontSize: '20px',
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              padding: '8px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = theme.colors.textSecondary;
            }}
          >
            ✕
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div style={{
          padding: isMobile ? '16px' : theme.spacing.lg,
          paddingTop: '12px',
          overflow: 'auto',
          flex: 1
        }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{ fontSize: '24px', animation: 'spin 1s linear infinite' }}>⚽</div>
            <p>Cargando perfil...</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            {/* FIFA Card del jugador */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: theme.borderRadius.card,
              padding: theme.spacing.lg,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing.md
                        }}>
              {userData ? (
                (() => {
                  const userForFifaCard = {
                    id: player.userId || '',
                    name: player.name,
                    email: userData?.email || '',
                    position: player.position,
                    rating: player.rating,
                    avatar: userData?.avatar || undefined,
                    preferredFoot: userData?.preferredFoot,
                    preferredPosition: userData?.preferredPosition
                  };

                  return (
                    <FifaCard 
                      user={userForFifaCard}
                      stats={playerStats ? {
                        totalGoals: playerStats.totalGoals || 0,
                        totalAssists: playerStats.totalAssists || 0,
                        totalSaves: playerStats.totalSaves || 0,
                        totalMatches: playerStats.totalMatches || 0,
                        totalWins: playerStats.totalWins || 0,
                        totalLosses: playerStats.totalLosses || 0,
                      } : null}
                    />
                  );
                })()
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div>Cargando datos del jugador...</div>
                </div>
              )}
              
              {/* Tags más recibidos debajo de la FIFA card */}
              {gamificationStats && (
                <div style={{
                  width: '100%',
                  textAlign: 'center'
                }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    color: theme.colors.primary,
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    🏷️ Tags Más Recibidos
                  </h4>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    {gamificationStats.topPositiveTags?.slice(0, 3).map((tag) => (
                      <span key={tag.tag} style={{
                        backgroundColor: '#00E676',
                        color: '#181A1B',
                        padding: '6px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        +{tag.tag}
                        <span style={{
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          {tag.count}
                        </span>
                      </span>
                    )) || []}
                    {gamificationStats.topNegativeTags?.slice(0, 1).map((tag) => (
                      <span key={tag.tag} style={{
                        backgroundColor: '#FF6B6B',
                        color: '#FFFFFF',
                        padding: '6px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        -{tag.tag}
                        <span style={{
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          {tag.count}
                        </span>
                      </span>
                    )) || []}
                  </div>
                </div>
              )}
            </div>

            {/* Estadísticas de Gamificación */}
            {gamificationStats && (
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: theme.borderRadius.card,
                padding: theme.spacing.lg,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  color: theme.colors.primary,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: 'bold'
                }}>
                  📊 Estadísticas de Reviews
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#00E676' }}>
                      {gamificationStats.totalReviewsReceived}
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Reviews Recibidas</div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7C4DFF' }}>
                      {gamificationStats.totalReviewsGiven}
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Reviews Dadas</div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFD600' }}>
                      {gamificationStats.positivePercentage}%
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Positivas</div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: gamificationStats.tagBalance >= 0 ? '#00E676' : '#FF6B6B' }}>
                      {gamificationStats.tagBalance}
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Balance</div>
                  </div>
                </div>
              </div>
            )}

            {/* Últimas Reviews */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: theme.borderRadius.card,
              padding: theme.spacing.lg,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                color: theme.colors.primary,
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 'bold'
              }}>
                ⭐ Últimas Reviews Recibidas
              </h3>
              
              {playerReviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textSecondary }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    {player.name} aún no tiene reviews
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  {playerReviews.map((review) => (
                    <div
                      key={review.id}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: theme.borderRadius.card,
                        padding: theme.spacing.md,
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing.sm
                      }}>
                        <div>
                          <div style={{
                            color: theme.colors.textSecondary,
                            fontSize: theme.fonts.sizeSmall
                          }}>
                            {/* Solo mostrar la fecha, no el reviewer */}
                            {formatReviewTimestamp(review.createdAt)}
                          </div>
                        </div>
                      </div>

                      {review.comment && (
                        <div style={{
                          marginBottom: theme.spacing.sm,
                          color: theme.colors.text,
                          fontFamily: theme.fonts.body,
                          fontSize: theme.fonts.sizeBody,
                          fontStyle: 'italic'
                        }}>
                          "{review.comment}"
                        </div>
                      )}

                      {/* Etiquetas positivas */}
                      {review.positiveTags.length > 0 && (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {review.positiveTags.map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  backgroundColor: getTagColor(tag, true),
                                  color: '#181A1B',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: theme.fonts.sizeSmall,
                                  fontWeight: theme.fonts.weightBold
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Etiquetas negativas */}
                      {review.negativeTags.length > 0 && (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {review.negativeTags.map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  backgroundColor: getTagColor(tag, false),
                                  color: '#FFFFFF',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: theme.fonts.sizeSmall,
                                  fontWeight: theme.fonts.weightBold
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tags personalizados positivos */}
                      {review.customPositiveTags && review.customPositiveTags.length > 0 && (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {review.customPositiveTags.map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  backgroundColor: '#00BFA5',
                                  color: '#FFFFFF',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: theme.fonts.sizeSmall,
                                  fontWeight: theme.fonts.weightBold,
                                  border: '1px solid #FFFFFF'
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tags personalizados negativos */}
                      {review.customNegativeTags && review.customNegativeTags.length > 0 && (
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {review.customNegativeTags.map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  backgroundColor: '#D32F2F',
                                  color: '#FFFFFF',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: theme.fonts.sizeSmall,
                                  fontWeight: theme.fonts.weightBold,
                                  border: '1px solid #FFFFFF'
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Etiqueta personalizada (legacy) */}

                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}; 